import { useCallback, useEffect, useRef } from 'react';
import { BrowserRouter, useLocation, useNavigationType } from 'react-router-dom';
import AppRoutes from '../routing';

/**
 * Inner component that uses router hooks for side effects.
 * Handles scroll-to-top on PUSH navigation and robots meta tag updates.
 */
function RouterEffects() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastHrefRef = useRef<string>('');
  const metaRef = useRef<Element | null>(null);

  // Robots meta tag logic
  useEffect(() => {
    if (!metaRef.current) {
      metaRef.current = document.querySelector('meta[name=robots]');
    }
    if (!metaRef.current) {
      return;
    }

    const pathname = location.pathname;
    if (pathname.startsWith('/gf/') || pathname.startsWith('/gd/')) {
      metaRef.current.setAttribute('content', 'noindex,nofollow');
    } else {
      metaRef.current.setAttribute('content', 'index,follow');
    }
  }, [location.pathname]);

  // Scroll to top on PUSH navigation when pathname changes
  useEffect(() => {
    document.body.classList.remove('errorPage');

    if (navigationType === 'PUSH' && lastHrefRef.current !== location.pathname) {
      (document.documentElement || document.body).scrollTop = 0;
    }
    lastHrefRef.current = location.pathname;
  }, [location.pathname, navigationType]);

  return null;
}

const RouterWrapper: React.FC = () => {
  const toggleEnvironment = useCallback(() => {
    // Environment switching is now handled in the routing layer
    return false;
  }, []);

  // Initialize domain setting (legacy compatibility)
  useEffect(() => {
    try {
      if (document.domain.indexOf('bankgutmann.co.at') > -1) {
        // @ts-ignore - document.domain is deprecated but needed for legacy
        document.domain = 'bankgutmann.co.at';
      }
    } catch (e) {
      // Ignore errors from document.domain assignment
    }
  }, []);

  return (
    <BrowserRouter>
      <RouterEffects />
      <AppRoutes toggleEnvironment={toggleEnvironment} />
    </BrowserRouter>
  );
};

export { RouterEffects };
export { RouterWrapper as default };
